import React, { useRef, useState } from 'react';
import Upload from 'lucide-react/dist/esm/icons/upload';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import X from 'lucide-react/dist/esm/icons/x';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import { getTranslation } from '../../../i18n';


const UploadSection = ({ onFileSelect, language, initialGuidance = '', isExtending = false }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userGuidance, setUserGuidance] = useState(initialGuidance);
  
  const t = (key) => getTranslation(language, key);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError('');
    const files = Array.from(e.dataTransfer.files);
    validateAndProcessFiles(files);
  };

  const handleChange = (e) => {
    e.preventDefault();
    setError('');
    const files = Array.from(e.target.files);
    validateAndProcessFiles(files);
  };

  const validateAndProcessFiles = (files) => {
    if (!files || files.length === 0) return;

    const maxSize = 32 * 1024 * 1024;
    const errors = [];

    for (const file of files) {
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: ${t('errorPdfOnly')}`);
      } else if (file.size > maxSize) {
        errors.push(`${file.name}: ${t('errorFileSize')}`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('; '));
      return;
    }

    setSelectedFiles(files);
  };

  const removeFile = (indexToRemove) => {
    const newFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(newFiles);
    if (newFiles.length === 0) setError('');
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      setError(t('errorNoFiles'));
      return;
    }
    onFileSelect(selectedFiles, userGuidance.trim() || null);
  };

  const handleClick = () => {
    if (selectedFiles.length === 0) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 animate-fade-in" data-walkthrough="upload">
      {/* Dropzone */}
      <div
        className={`relative p-12 rounded-3xl cursor-pointer transition-all duration-300 border-2 border-dashed backdrop-blur-xl ${
          dragActive 
            ? 'border-brand bg-brand/10 scale-[1.02] shadow-[0_0_40px_rgba(33,158,188,0.3)]' 
            : selectedFiles.length > 0 
              ? 'border-status-success/40 bg-status-success/5 cursor-default' 
              : 'border-strong bg-surface-primary hover:border-brand/50 hover:bg-brand/5'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,rgba(33,158,188,0.15)_0%,transparent_70%)] pointer-events-none animate-pulse" />
        
        <div className="relative z-10 flex flex-col items-center text-center">
          {selectedFiles.length === 0 ? (
            <>
              <div className="relative mb-8">
                <img 
                  src="/quazi-glyph.svg" 
                  alt="QUAZI" 
                  className="w-20 h-20 drop-shadow-[0_0_30px_rgba(33,158,188,0.6)] animate-float" 
                />
              </div>
              
              <h2 className="text-2xl font-bold text-content mb-3 tracking-wide">
                {isExtending ? (t('extendAnalysis') || 'Extend Analysis') : t('uploadTitle')}
              </h2>
              <p className="text-base text-content-secondary mb-8 max-w-md leading-relaxed">
                {isExtending 
                  ? (t('extendAnalysisSubtitle') || 'Upload additional documents to enhance and complete this analysis')
                  : t('uploadSubtitleMultiple')
                }
              </p>
              
              {isExtending && (
                <div className="mb-6 px-4 py-3 bg-violet-500/10 border border-violet-500/30 rounded-xl inline-flex items-center gap-2">
                  <Sparkles size={16} className="text-violet-400" />
                  <span className="text-sm text-violet-300">
                    {t('extendingHint') || 'Adding documents to existing analysis for more complete results'}
                  </span>
                </div>
              )}

              <button 
                type="button"
                className="inline-flex items-center gap-3 py-4 px-8 bg-gradient-to-br from-brand to-brand-hover border-none rounded-2xl text-content-inverse text-base font-semibold cursor-pointer shadow-glow transition-all duration-300 hover:-translate-y-1 hover:shadow-glow-strong"
              >
                <Upload size={20} />
                {t('uploadButton')}
              </button>

              <p className="mt-6 text-sm text-content-muted">{t('uploadLimitMultiple')}</p>
            </>
          ) : (
            <div className="w-full">
              <div className="flex items-center justify-center gap-3 mb-6">
                <FileText size={24} className="text-status-success" />
                <h3 className="text-xl font-semibold text-content">
                  {getTranslation(language, 'filesSelected', { count: selectedFiles.length })}
                </h3>
              </div>
              <div className="flex flex-col gap-2 mb-6 max-h-[200px] overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-3 py-3 px-4 bg-surface-primary border border-default rounded-xl"
                  >
                    <FileText size={18} className="text-primary flex-shrink-0" />
                    <span className="flex-1 text-sm text-content font-medium truncate">{file.name}</span>
                    <span className="text-xs text-content-muted flex-shrink-0">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                    <button
                      type="button"
                      className="w-7 h-7 flex items-center justify-center bg-transparent border border-default rounded-lg text-content-muted cursor-pointer transition-all duration-200 hover:bg-status-error/20 hover:border-status-error/50 hover:text-status-error"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      aria-label={t('removeFile')}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
              {/* User Guidance Input */}
              <div className="w-full mb-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare size={16} className="text-primary" />
                  <span className="text-sm font-medium text-content-secondary">{t('analysisGuidanceLabel')}</span>
                  <span className="text-xs text-content-muted ml-1">({t('optional')})</span>
                </div>
                <textarea
                  value={userGuidance}
                  onChange={(e) => setUserGuidance(e.target.value)}
                  placeholder={t('analysisGuidancePlaceholder')}
                  className="w-full h-24 px-4 py-3 bg-surface-primary border border-default rounded-xl text-sm text-content placeholder:text-content-muted resize-none focus:outline-none focus:border-brand/50 focus:bg-surface-secondary transition-all duration-200"
                  maxLength={1000}
                />
                <p className="text-xs text-content-muted mt-1.5">
                  {t('analysisGuidanceHint')}
                </p>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-transparent border border-strong rounded-xl text-content text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-surface-primary hover:border-strong"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <Upload size={18} />
                  {t('addMoreFiles')}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-gradient-to-br from-status-success to-emerald-600 border-none rounded-xl text-content-inverse text-sm font-semibold cursor-pointer shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpload();
                  }}
                >
                  <Sparkles size={18} />
                  {t('analyzeFiles')}
                </button>
              </div>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          onChange={handleChange}
          className="hidden"
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 mt-6 p-4 bg-status-error/10 border border-status-error/30 rounded-xl animate-fade-in">
          <AlertCircle size={20} className="text-status-error flex-shrink-0" />
          <span className="text-sm text-status-error-light">{error}</span>
        </div>
      )}

      {/* Upload Instructions */}
      <div className="mt-6 p-6 bg-brand/5 border border-brand/20 rounded-2xl animate-fade-in">
        <div className="flex items-center gap-3 mb-3">
          <FileText size={20} className="text-primary" />
          <h3 className="text-base font-semibold text-content">{t('uploadInstructions')}</h3>
        </div>
        <p className="text-sm text-content-secondary leading-relaxed m-0">{t('uploadInstructionsTextMultiple')}</p>
      </div>

      {/* Quality Disclaimer */}
      <div className="mt-4 p-6 bg-brand/5 border border-brand/20 rounded-2xl animate-fade-in">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles size={20} className="text-brand" />
          <h3 className="text-base font-semibold text-content">{t('qualityDisclaimer')}</h3>
        </div>
        <p className="text-sm text-content-secondary leading-relaxed m-0">{t('qualityDisclaimerText')}</p>
      </div>
    </div>
  );
};

export default UploadSection;
